import { DataProxy } from 'apollo-cache'
import { FetchResult } from 'apollo-link'
import { ErrorHandler } from '@sketch/tracing'
import { useApolloClient } from 'react-apollo'

import {
  useMoveShareToProjectMutation,
  MoveShareToProjectMutation,
  MoveShareToProjectMutationVariables,
  ProjectFragment,
  ShareInfoFragment,
  ShareListItemFragment,
  DataWithoutUserErrors,
} from '@sketch/gql-types'

import { updateProjectList, readShareWithProjectFragment } from './utils'
import { useFlag, removeFromPaginated } from '@sketch/modules-common'
import { noop } from '@sketch/utils'

interface MoveShareEventHandlers {
  onError: (error: string) => void
  onCompleted: (project: ProjectFragment) => void
}

export const useMoveShareToProject = ({
  onError = noop,
  onCompleted = noop,
}: MoveShareEventHandlers) => {
  // TODO: Remove collections FF
  // https://github.com/sketch-hq/Cloud/issues/15999
  const isCollectionsFFEnabled = useFlag('collections')

  const client = useApolloClient()
  const handleOnCompleted = (
    data: DataWithoutUserErrors<MoveShareToProjectMutation>
  ) => {
    if (!data?.moveShareToProject?.project) {
      onError(
        'An unexpected error occurred while moving document to a project.'
      )
      ErrorHandler.shouldNeverHappen.invalidMutationData('moveShareToProject')
      return
    }

    const { project } = data.moveShareToProject

    onCompleted(project)
  }

  const handleUpdate = (
    cachedShare: ShareInfoFragment | ShareListItemFragment | null,
    cache: DataProxy,
    { data }: FetchResult<MoveShareToProjectMutation>
  ) => {
    if (
      !data ||
      !data.moveShareToProject.successful ||
      !data.moveShareToProject.project ||
      !data.moveShareToProject.share
    ) {
      return ErrorHandler.shouldNeverHappen(
        'Move share to project should always have data'
      )
    }

    const { project, share: updatedShare } = data.moveShareToProject

    const updatedShareInCache = readShareWithProjectFragment(
      cache,
      updatedShare.identifier
    )

    // Prevent the undefined if the share isn't found
    if (!cachedShare || !updatedShare || !updatedShareInCache) {
      ErrorHandler.shouldNeverHappen(
        'there always should be a share when moving between projects'
      )
      return
    }

    if (cachedShare?.project?.identifier) {
      // Update the previous cached project, if there's one
      removeFromPaginated(cache, updatedShare!, cachedShare.project)
    } else {
      removeFromPaginated(
        cache,
        updatedShare!,
        key => key.includes('.shares') && key.includes('"filter":"NO_PROJECT"')
      )
    }

    // Update the new project with the new share.
    updateProjectList(
      cache,
      {
        projectId: project.identifier,
        filters: isCollectionsFFEnabled
          ? ['NO_COLLECTION' as const]
          : undefined,
      },
      ({ project }) => {
        const { shares } = project
        // no need to have a collection defined
        shares.entries?.push({ ...updatedShareInCache, collection: null })
        shares.entries?.sort(
          (current, next) =>
            Date.parse(next.version?.createdAt as string) -
            Date.parse(current.version?.createdAt as string)
        )
        shares.meta.totalCount += 1
      }
    )
  }

  const [mutate, ...rest] = useMoveShareToProjectMutation({
    redirectErrors: true,
    onError: error => {
      onError(error.message)
    },
    onCompleted: handleOnCompleted,
  })

  return [
    (variables: MoveShareToProjectMutationVariables) => {
      const cachedShare = readShareWithProjectFragment(
        client,
        variables.shareId
      )
      if (cachedShare?.project?.identifier === variables.projectId) {
        return
      }
      return mutate({
        variables,
        update: (...args) => handleUpdate(cachedShare, ...args),
      })
    },
    ...rest,
  ] as const
}
